import { executionMode } from '../../constants/constants';

/**
 * Which readouts the runtime shows, derived from the two axes that actually
 * decide it:
 *
 *   execution_mode  what the exercise was prescribed for (MOTION_CAPTURE /
 *                   NO_MOTION_CAPTURE) — the prescription writes it, the backend
 *                   restates it on every intro payload and every frame, and
 *                   action 7 can flip it for one exercise.
 *   camera          whether a live media source is actually feeding detection
 *                   (a camera, or an uploaded clip playing) — only the client
 *                   knows this, and it can disagree with the mode.
 *
 * The four combinations:
 *
 *   MOTION_CAPTURE    + camera  -> online : the full tracking UI
 *   MOTION_CAPTURE    + no cam  -> offline: plus a warning — the exercise expects
 *                                 a camera, so nothing is scoring it
 *   NO_MOTION_CAPTURE + camera  -> offline: the backend scores nothing, so a live
 *                                 feed with a skeleton would read as tracking
 *                                 that is not happening
 *   NO_MOTION_CAPTURE + no cam  -> offline
 *
 * Offline keeps the countdown, the prescribed totals and the reference video.
 * Everything pose-derived (skeleton, framing border, quality, hold ring, score
 * ribbon) is hidden, because there is no pose behind it.
 *
 * Deliberately its own module rather than part of features/exercise/helpers.js:
 * that file imports @mediapipe/tasks-vision, which has no business in a unit test
 * or in a light readout component (see exercise-intro/info-bar.jsx for the same
 * reasoning).
 */
export const RUNTIME_DISPLAY_MODE = Object.freeze({
  online: 'online',
  offline: 'offline',
});

export function resolveRuntimeDisplay({ isMotionCapture, hasCamera } = {}) {
  const motionCapture = Boolean(isMotionCapture);
  const camera = Boolean(hasCamera);
  const isOnline = motionCapture && camera;

  return {
    mode: isOnline ? RUNTIME_DISPLAY_MODE.online : RUNTIME_DISPLAY_MODE.offline,
    isOnline,
    isOffline: !isOnline,
    // Only the MOTION_CAPTURE-without-a-camera case: the exercise was prescribed
    // for tracking and nothing is backing it, which is worth telling the user.
    isCameraMissingForMotionCapture: motionCapture && !camera,
    // The live rep count comes off the socket and only means something while the
    // camera is being scored; offline shows the prescribed target instead.
    showsLiveCounter: isOnline,
    showsTargetCounter: !isOnline,
    showsQuality: isOnline,
    showsCameraCell: isOnline,
    showsHoldRing: isOnline,
  };
}

/**
 * The value sent as `cameraAvailable` on the WS config message: whether a live
 * media source is feeding detection right now. Kept beside the resolver so the
 * flag on the wire and the flag driving the UI can't drift apart.
 *
 * Deliberately the device-level truth (`cameraIsActive`), not the page's own
 * `cameraAvailable`, which also folds in the prescribed mode and the MediaPipe
 * toggle: the backend already knows the mode, and what it cannot know is whether
 * a camera exists at all.
 */
export function resolveCameraAvailableForConfig(state) {
  return Boolean(state?.cameraIsActive);
}

export function isNoMotionCapture(value) {
  return value === executionMode.noMotionCapture;
}
